/**
 * Provides a hook to access the session storage.
 */
import { useCallback } from 'react';
import * as s from 'superstruct';

import {
  identifierInputValueGuard,
  ssoConnectorMetadataGuard,
  verificationIdsMapGuard,
} from '@/types/guard';
import {
  getStorageWithTtl,
  removeFromBothStorages,
  setStorageWithTtl,
} from '@/utils/social-connectors';

const logtoStorageKeyPrefix = `logto:${window.location.origin}`;

export enum StorageKeys {
  SsoEmail = 'sso-email',
  SsoConnectors = 'sso-connectors',
  IdentifierInputValue = 'identifier-input-value',
  ForgotPasswordIdentifierInputValue = 'forgot-password-identifier-input-value',
  verificationIds = 'verification-ids',
}

const valueGuard = Object.freeze({
  [StorageKeys.SsoEmail]: s.string(),
  [StorageKeys.SsoConnectors]: s.array(ssoConnectorMetadataGuard),
  [StorageKeys.IdentifierInputValue]: identifierInputValueGuard,
  [StorageKeys.ForgotPasswordIdentifierInputValue]: identifierInputValueGuard,
  [StorageKeys.verificationIds]: verificationIdsMapGuard,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we  don't care about the superstruct details
} satisfies { [key in StorageKeys]: s.Struct<any> });

type StorageValueType<K extends StorageKeys> = s.Infer<(typeof valueGuard)[K]>;

/**
 * Keys that must survive a browser context switch.
 *
 * On mobile the social sign-in round trip leaves the browser (LINE / Facebook app-to-app)
 * and the callback can come back in a fresh tab, where `sessionStorage` is empty but
 * `localStorage` is not. `social_auth_state` already handles this; the verification id has
 * to as well, otherwise the callback passes the state check and then fails the session check
 * with `error.invalid_session`.
 *
 * ponytail: only this key is mirrored. The identifier inputs stay per-tab on purpose, they
 * hold the user's email / phone and have no reason to outlive the tab.
 */
const crossContextKeys = new Set<StorageKeys>([StorageKeys.verificationIds]);

const useSessionStorage = () => {
  const set = useCallback(<T extends StorageKeys>(key: T, value: StorageValueType<T>) => {
    const storageKey = `${logtoStorageKeyPrefix}:${key}`;
    const write = crossContextKeys.has(key)
      ? (rawValue: string) => {
          setStorageWithTtl(storageKey, rawValue);
        }
      : (rawValue: string) => {
          sessionStorage.setItem(storageKey, rawValue);
        };

    if (typeof value === 'object') {
      write(JSON.stringify(value));
      return;
    }

    write(value);
  }, []);

  const remove = useCallback((key: StorageKeys) => {
    const storageKey = `${logtoStorageKeyPrefix}:${key}`;

    if (crossContextKeys.has(key)) {
      removeFromBothStorages(storageKey);
      return;
    }

    sessionStorage.removeItem(storageKey);
  }, []);

  const get = useCallback(
    <T extends StorageKeys>(key: T): StorageValueType<T> | undefined => {
      const storageKey = `${logtoStorageKeyPrefix}:${key}`;
      const value = crossContextKeys.has(key)
        ? getStorageWithTtl(storageKey)
        : sessionStorage.getItem(storageKey);

      if (value === null) {
        return;
      }

      const [error, rawValue] = valueGuard[key].validate(
        (() => {
          try {
            // eslint-disable-next-line no-restricted-syntax -- we use superstruct to validate the value
            return JSON.parse(value) as unknown;
          } catch {
            return value;
          }
        })()
      );

      if (error) {
        // Clear the invalid value
        remove(key);
        return;
      }

      return rawValue;
    },
    [remove]
  );

  return { set, get, remove };
};

export default useSessionStorage;
